import { useState, useCallback, useEffect } from "react";
import { GetUpcomingRaves } from "@/server/Raves";

export const useUpcomingRaves = () => {
  const [hasUpcomingRaves, setHasUpcomingRaves] = useState(false);
  const [upcomingRaveDetails, setUpcomingRaveDetails] = useState<{
    id: string;
    name: string;
    location: string;
    date: string;
  } | null>(null);

  useEffect(() => {
    let isMounted = true;
    const checkUpcomingRaves = async () => {
      try {
        // Cache duration set to 20 minutes (1200000 milliseconds)
        const cachedRaves = localStorage.getItem("cachedUpcomingRaves");
        const cachedTimestamp = localStorage.getItem(
          "cachedUpcomingRavesTimestamp"
        );

        if (cachedRaves && cachedTimestamp) {
          const cachedData = JSON.parse(cachedRaves);
          const timestamp = parseInt(cachedTimestamp, 10);

          if (Date.now() - timestamp < 1200000) {
            if (isMounted) {
              setHasUpcomingRaves(cachedData.length > 0);
              if (cachedData.length > 0) {
                setUpcomingRaveDetails({
                  id: cachedData[0].id,
                  name: cachedData[0].name,
                  location: cachedData[0].location,
                  date: formatDate(cachedData[0].date.toString()),
                });
              }
            }
            return;
          }
        }

        const upcomingRaves = await GetUpcomingRaves();
        if (isMounted) {
          setHasUpcomingRaves(upcomingRaves.length > 0);
          if (upcomingRaves.length > 0) {
            setUpcomingRaveDetails({
              id: upcomingRaves[0].id,
              name: upcomingRaves[0].name,
              location: upcomingRaves[0].location,
              date: formatDate(upcomingRaves[0].date.toString()),
            });

            localStorage.setItem(
              "cachedUpcomingRaves",
              JSON.stringify(upcomingRaves)
            );
            localStorage.setItem(
              "cachedUpcomingRavesTimestamp",
              Date.now().toString()
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch upcoming raves:", error);
      }
    };

    checkUpcomingRaves();
    return () => {
      isMounted = false;
    };
  }, []);

  return { hasUpcomingRaves, upcomingRaveDetails };
};

// Date formatting function
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    })
    .replace(",", "");
};

export const useLocalOrders = () => {
  const [hasOrders, setHasOrders] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const checkOrders = () => {
      try {
        const orders = JSON.parse(localStorage.getItem("orders") || "[]");
        if (isMounted) {
          setHasOrders(orders.length > 0);
        }
      } catch (error) {
        console.error("Failed to parse orders:", error);
      }
    };

    checkOrders();
    return () => {
      isMounted = false;
    };
  }, []);

  return { hasOrders };
};
